import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** An engagement row (field names read defensively via the accessors below). */
export interface Engagement {
  id: number;
  name?: string | null;
  description?: string | null;
  status?: string | null; // "Active" | "Upcoming" | "Completed" | "Closed"
  engagement_date?: string | null;
  owner?: number | { id?: number; full_name?: string; name?: string } | null;
  owner_name?: string | null;
  ec_location_level_id?: number | null;
  area_code?: string | null;
  boundary?: string | null;
}

/** A user returned by /organisation/users-in-area/ (owner dropdown). */
export interface AreaUser {
  id: number;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
}

/** Request body for POST /engagement/. */
export interface CreateEngagementPayload {
  name: string;
  description: string;
  status: string;
  engagement_date: string;
  owner: number;
  ec_location_level_id: number;
  area_code: string;
}

/** Engagement counts per status, for the filter cards. */
export interface EngagementStats {
  all: number;
  active: number;
  upcoming: number;
  completed: number;
  closed: number;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result: current page rows plus the server's total count. */
export interface Paginated<T> {
  items: T[];
  total: number;
}
const toPaginated = <T>(r: Listish<T>): Paginated<T> => {
  const items = asList(r);
  const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
  return { items, total };
};

const toNum = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0);

/**
 * Normalize the /engagement/stats/ response into fixed count keys. The backend
 * shape isn't guaranteed, so accept either an array of `{ status, count }` rows
 * or an object keyed by status name (any casing), with an optional total.
 */
const toStats = (r: unknown): EngagementStats => {
  const map: Record<string, number> = {};
  let total = 0;
  if (Array.isArray(r)) {
    for (const row of r as Array<Record<string, unknown>>) {
      const k = String(row.status ?? row.name ?? '').toLowerCase();
      const c = toNum(row.count ?? row.total ?? row.value);
      if (k) map[k] = c;
      total += c;
    }
  } else if (r && typeof r === 'object') {
    for (const [k, v] of Object.entries(r as Record<string, unknown>)) {
      map[k.toLowerCase()] = toNum(v);
    }
  }
  const active = map.active ?? 0;
  const upcoming = map.upcoming ?? 0;
  const completed = map.completed ?? 0;
  const closed = map.closed ?? 0;
  const explicit = map.all ?? map.total ?? map.count ?? 0;
  return {
    all: explicit || total || active + upcoming + completed + closed,
    active,
    upcoming,
    completed,
    closed,
  };
};

/* ------------------------------------------------------------------ *
 * Defensive accessors (exact field names aren't guaranteed).
 * ------------------------------------------------------------------ */
export const engName = (e: Engagement) => e.name || 'Untitled';
export const engStatus = (e: Engagement) => (e.status || 'active').toLowerCase();
export const engDate = (e: Engagement) => e.engagement_date || '';
export const engArea = (e: Engagement) => e.area_code || e.boundary || '';
export const engOwnerName = (e: Engagement): string => {
  if (e.owner_name) return e.owner_name;
  if (e.owner && typeof e.owner === 'object') return e.owner.full_name || e.owner.name || '';
  return '';
};
export const areaUserName = (u: AreaUser) => u.full_name || u.name || u.email || `User ${u.id}`;

export const engagementsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getEngagements: build.query<
      Paginated<Engagement>,
      { search?: string; status?: string; page?: number; pageSize?: number } | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.engagement}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.status ? { status: arg.status } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<Engagement>) => toPaginated(r),
      providesTags: ['Engagements'],
    }),
    getEngagementStats: build.query<EngagementStats, void>({
      query: () => ({ url: `${ApiEndPoints.engagement}/stats/` }),
      transformResponse: (r: unknown) => toStats(r),
      providesTags: ['Engagements'],
    }),
    getEngagement: build.query<Engagement, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.engagement}/${id}/` }),
      providesTags: ['Engagements'],
    }),
    createEngagement: build.mutation<Engagement, CreateEngagementPayload>({
      query: (body) => ({ url: `${ApiEndPoints.engagement}/`, method: 'POST', body }),
      invalidatesTags: ['Engagements'],
    }),
    updateEngagement: build.mutation<
      Engagement,
      { id: number | string; patch: Partial<CreateEngagementPayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.engagement}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Engagements'],
    }),
    deleteEngagement: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.engagement}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Engagements'],
    }),
    // Owner dropdown — users inside the selected boundary.
    getUsersInArea: build.query<AreaUser[], { areaCode: string }>({
      query: ({ areaCode }) => ({
        url: `${ApiEndPoints.usersInArea}/`,
        params: { area_code: areaCode },
      }),
      transformResponse: (r: Listish<AreaUser>) => asList(r),
    }),
  }),
});

export const {
  useGetEngagementsQuery,
  useGetEngagementStatsQuery,
  useGetEngagementQuery,
  useCreateEngagementMutation,
  useUpdateEngagementMutation,
  useDeleteEngagementMutation,
  useGetUsersInAreaQuery,
} = engagementsApi;
